import { MediaCollection, Measurement, Region } from "@/gql/graphql";
import { useSettingsSupplier } from "@/app/Suppliers/Utils/useSettingsSupplier";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { PhoneInput } from "@/lib/Components/Form/Inputs/PhoneInput";
import { NumberInput } from "@/lib/Components/Form/Inputs/NumberInput";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { DistanceAllowedInput } from "@/lib/Components/Form/Inputs/DistanceAllowedInput";
import { RichTextInput } from "@/lib/Components/Form/Inputs/RichTextInput";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import {
  EditableMultilineSettingRow,
  EditableRowsProvider,
  DropzoneSettingRow,
  EditableSettingRow,
  SettingRowsCard,
  StaticSettingRow,
} from "@/app/Settings/Settings/Components/EditableSettingRow";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateSupplierMutation } from "@/app/Suppliers/GraphQL/supplierMutations";
import { RawText } from "@/lib/Components/RawText";
import { stripHtml } from "@/lib/Utils/string";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { supplierValidator } from "@/app/Suppliers/Validation/supplierValidator";
import { useSupplierImageDropzone } from "@/app/Suppliers/Hooks/useSupplierImageDropzone";
import { SupplierImagePreview } from "@/app/Suppliers/Components/SupplierImageDropzone";
import { SupplierRecord } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { AdminSection } from "@/lib/Components/Common/AdminSection";
import { SupplierLineSoldForm } from "@/app/Suppliers/Forms/SupplierLineSoldForm";
import {
  CurrencyTypeInput,
  currencyLabel,
} from "@/app/Common/Components/CurrencyTypeInput";
import {
  HireUnitTypeInput,
  hireUnitLabel,
} from "@/app/Common/Components/HireUnitType";
import {
  MeasurementInput,
  measurementLabel,
} from "@/app/Common/Components/MeasurementInput";
import { NoSupplierEmptyState } from "@/app/Settings/Settings/Components/NoSupplierEmptyState";
import { SupplierAcknowledgementGrid } from "@/app/Suppliers/Components/SupplierAcknowledgementGrid";
import { SupplierAdminContactGrid } from "@/app/Suppliers/Components/SupplierAdminContactGrid";
import { TermsAndConditionsSettingsCard } from "@/app/Settings/Settings/Components/TermsAndConditionsSettingsCard";
import { SettingCard } from "@/app/Settings/Settings/Components/SettingFormCard";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { useTranslations } from "use-intl";

export function SettingsGeneralScreen() {
  const { supplier } = useSettingsSupplier();
  const { mutateAsync } = useGqlMutation(updateSupplierMutation);
  const t = useTranslations("settings.general");
  const tExtras = useTranslations("settings.extras");
  const tRelocation = useTranslations("relocation");
  const tBoolean = useTranslations("common.boolean");

  if (!supplier) {
    return <NoSupplierEmptyState />;
  }

  const salesAgent = supplier.managedBy?.salesAgent;
  const hireUnitName = tRelocation(`hire_unit_type.${supplier.hire_unit_type}`);
  const measurementUnit =
    supplier.measurement === Measurement.Metric ? "Kms" : "Mls";
  const excessMeasurementUnit =
    supplier.measurement === Measurement.Metric ? "km" : "ml";

  return (
    <EditableRowsProvider>
      <div className="space-y-8">
        <AdminSection containerClassName="bg-white">
          <div className="space-y-8">
            <SettingRowsCard
              title="Account management"
              description="Who to contact internally about this supplier."
            >
              <StaticSettingRow
                label="Organisation"
                value={supplier.managedBy?.name}
                emptyLabel="Unassigned"
              />
              <StaticSettingRow
                label="Sales agent"
                value={
                  salesAgent ? <SalesAgent salesAgent={salesAgent} /> : null
                }
                emptyLabel="Unassigned"
              />
            </SettingRowsCard>

            <SettingRowsCard title="Admin notes">
              <EditableMultilineSettingRow
                label="Contact notes"
                value={
                  supplier.contact_notes ? (
                    <RawText className="prose-sm max-w-none">
                      {supplier.contact_notes}
                    </RawText>
                  ) : undefined
                }
                editClassName="max-w-2xl"
                initialValues={{ contact_notes: supplier.contact_notes }}
                onSave={(values) =>
                  mutateAsync({ id: supplier.id, input: values })
                }
              >
                <RichTextInput
                  name="contact_notes"
                  label="Contact notes"
                  className="col-span-full"
                />
              </EditableMultilineSettingRow>
            </SettingRowsCard>

            <SettingCard
              title="Contacts"
              description="People to contact at this supplier. Never shown to the supplier or to customers."
            >
              <SupplierAdminContactGrid supplierId={supplier.id} />
            </SettingCard>
          </div>
        </AdminSection>

        <SettingRowsCard
          title={t("profile.title")}
          description={t("profile.description")}
        >
          <EditableSettingRow
            label={t("profile.nameLabel")}
            value={supplier.name}
            initialValues={{ name: supplier.name }}
            validationSchema={toFormikValidationSchema(
              supplierValidator.pick({ name: true }),
            )}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <TextInput name="name" label={t("profile.nameLabel")} />
          </EditableSettingRow>

          <SupplierImageRow
            supplier={supplier}
            media={supplier.logo}
            collection={MediaCollection.Logo}
            label={t("profile.logoLabel")}
            hint={t("profile.logoHint")}
            frameClassName="h-10 w-36 rounded-md"
          />
          <SupplierImageRow
            supplier={supplier}
            media={supplier.icon}
            collection={MediaCollection.Icon}
            label={t("profile.iconLabel")}
            hint={t("profile.iconHint")}
            frameClassName="size-10 rounded-md"
          />
        </SettingRowsCard>

        <SettingRowsCard
          title={t("contactInformation.title")}
          description={t("contactInformation.description")}
        >
          <EditableSettingRow
            label={t("contactInformation.emailLabel")}
            value={supplier.email}
            initialValues={{ email: supplier.email }}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <TextInput
              type="email"
              name="email"
              label={t("contactInformation.emailLabel")}
              optionalLabel
              tooltip={t("contactInformation.emailTooltip")}
            />
          </EditableSettingRow>

          <EditableSettingRow
            label={t("contactInformation.phoneLabel")}
            value={supplier.phone}
            initialValues={{ phone: supplier.phone }}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <PhoneInput
              name="phone"
              label={t("contactInformation.phoneLabel")}
              optionalLabel
              tooltip={t("contactInformation.phoneTooltip")}
              defaultRegion={Region.Au}
            />
          </EditableSettingRow>
        </SettingRowsCard>

        <SettingRowsCard
          title={t("defaults.title")}
          description={t("defaults.description")}
        >
          <EditableSettingRow
            label={t("defaults.currencyLabel")}
            value={currencyLabel(supplier.currency)}
            initialValues={{ currency: supplier.currency }}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <CurrencyTypeInput
              name="currency"
              label={t("defaults.currencyLabel")}
            />
          </EditableSettingRow>

          <EditableSettingRow
            label={t("defaults.measurementLabel")}
            value={measurementLabel(supplier.measurement)}
            initialValues={{ measurement: supplier.measurement }}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <MeasurementInput
              name="measurement"
              label={t("defaults.measurementLabel")}
            />
          </EditableSettingRow>

          <EditableSettingRow
            label={t("defaults.hireUnitLabel")}
            value={hireUnitLabel(supplier.hire_unit_type)}
            initialValues={{ hire_unit_type: supplier.hire_unit_type }}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <HireUnitTypeInput
              name="hire_unit_type"
              label={t("defaults.hireUnitLabel")}
              tooltip={t("defaults.hireUnitTooltip")}
            />
          </EditableSettingRow>

          <EditableSettingRow
            label={tExtras("extraHireUnits.defaultExtraLabel", {
              hireUnitLabel: hireUnitName,
            })}
            value={supplier.default_extra_hire_units_allowed}
            initialValues={{
              default_extra_hire_units_allowed:
                supplier.default_extra_hire_units_allowed,
            }}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <NumberInput
              name="default_extra_hire_units_allowed"
              label={tExtras("extraHireUnits.defaultExtraLabel", {
                hireUnitLabel: hireUnitName,
              })}
              inputProps={{ min: 0 }}
            />
          </EditableSettingRow>

          <EditableSettingRow
            label={tExtras("extraHireUnits.distancePerExtraLabel", {
              measurement: measurementUnit,
              hireUnitLabel: hireUnitName,
            })}
            value={supplier.extra_distance_allowed_per_extra_hire_unit}
            emptyLabel="Unlimited"
            initialValues={{
              extra_distance_allowed_per_extra_hire_unit:
                supplier.extra_distance_allowed_per_extra_hire_unit,
            }}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <DistanceAllowedInput
              name="extra_distance_allowed_per_extra_hire_unit"
              label={tExtras("extraHireUnits.distancePerExtraLabel", {
                measurement: measurementUnit,
                hireUnitLabel: hireUnitName,
              })}
              measurement={supplier.measurement}
            />
          </EditableSettingRow>

          <EditableSettingRow
            label={tExtras("excessMileage.title")}
            value={
              supplier.excess_distance_rate
                ? `${formatCurrency(supplier.excess_distance_rate, supplier.currency)}/${excessMeasurementUnit}`
                : null
            }
            initialValues={{
              excess_distance_rate: supplier.excess_distance_rate,
            }}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <CurrencyInput
              currency={supplier.currency}
              name={"excess_distance_rate"}
              label={tExtras("excessMileage.costPerExcessLabel", {
                measurement: excessMeasurementUnit,
              })}
            />
          </EditableSettingRow>

          <EditableSettingRow
            label={t("defaults.collectAddressLabel")}
            value={tBoolean(
              supplier.collect_address_on_bookings ? "true" : "false",
            )}
            initialValues={{
              collect_address_on_bookings: supplier.collect_address_on_bookings,
            }}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <SwitchInput
              name="collect_address_on_bookings"
              label={t("defaults.collectAddressLabel")}
              tooltip={t("defaults.collectAddressTooltip")}
            />
          </EditableSettingRow>

          <EditableSettingRow
            label={t("defaults.collectGenderLabel")}
            value={tBoolean(
              supplier.collect_gender_on_bookings ? "true" : "false",
            )}
            initialValues={{
              collect_gender_on_bookings: supplier.collect_gender_on_bookings,
            }}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <SwitchInput
              name="collect_gender_on_bookings"
              label={t("defaults.collectGenderLabel")}
              tooltip={t("defaults.collectGenderTooltip")}
            />
          </EditableSettingRow>

          <EditableSettingRow
            label={t("defaults.relistOnCancellationLabel")}
            value={tBoolean(supplier.relist_on_cancellation ? "true" : "false")}
            initialValues={{
              relist_on_cancellation: supplier.relist_on_cancellation,
            }}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <SwitchInput
              name="relist_on_cancellation"
              label={t("defaults.relistOnCancellationLabel")}
              tooltip={t("defaults.relistOnCancellationTooltip")}
            />
          </EditableSettingRow>
        </SettingRowsCard>

        <TermsAndConditionsSettingsCard supplierId={supplier.id} />

        <SettingCard
          title={t("acknowledgements.title")}
          description={t("acknowledgements.description")}
        >
          <SupplierAcknowledgementGrid supplierId={supplier.id} />
        </SettingCard>

        <SettingRowsCard
          title={tExtras("standardInclusions.title")}
          description={tExtras("standardInclusions.description")}
        >
          <EditableSettingRow
            label={tExtras("standardInclusions.label")}
            value={
              supplier.standard_inclusions
                ? stripHtml(supplier.standard_inclusions)
                : undefined
            }
            editClassName="max-w-2xl"
            initialValues={{
              standard_inclusions: supplier.standard_inclusions,
            }}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <RichTextInput
              name={"standard_inclusions"}
              label={tExtras("standardInclusions.label")}
              className="col-span-full"
            />
          </EditableSettingRow>
        </SettingRowsCard>

        <SettingRowsCard
          title={tExtras("customerInstructions.title")}
          description={tExtras("customerInstructions.description")}
        >
          <EditableMultilineSettingRow
            label={tExtras("preCheckinInstructions.label")}
            value={supplier.pre_checkin_instructions}
            editClassName="max-w-2xl"
            initialValues={{
              pre_checkin_instructions: supplier.pre_checkin_instructions,
            }}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <TextAreaInput
              name={"pre_checkin_instructions"}
              label={tExtras("preCheckinInstructions.label")}
              description={tExtras("preCheckinInstructions.description")}
              className="col-span-full"
              textAreaProps={{ rows: 4 }}
            />
          </EditableMultilineSettingRow>

          <EditableMultilineSettingRow
            label={tExtras("paymentInstructions.label")}
            value={supplier.payment_instructions}
            editClassName="max-w-2xl"
            initialValues={{
              payment_instructions: supplier.payment_instructions,
            }}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <TextAreaInput
              name={"payment_instructions"}
              label={tExtras("paymentInstructions.label")}
              description={tExtras("paymentInstructions.description")}
              className="col-span-full"
              textAreaProps={{ rows: 4 }}
            />
          </EditableMultilineSettingRow>

          <EditableMultilineSettingRow
            label={tExtras("fuelRefundInstructions.label")}
            value={supplier.fuel_refund_instructions}
            editClassName="max-w-2xl"
            initialValues={{
              fuel_refund_instructions: supplier.fuel_refund_instructions,
            }}
            onSave={(values) => mutateAsync({ id: supplier.id, input: values })}
          >
            <TextAreaInput
              name={"fuel_refund_instructions"}
              label={tExtras("fuelRefundInstructions.label")}
              description={tExtras("fuelRefundInstructions.description")}
              className="col-span-full"
              textAreaProps={{ rows: 4 }}
            />
          </EditableMultilineSettingRow>
        </SettingRowsCard>

        <AdminSection containerClassName="bg-white">
          <div className="space-y-8">
            <SettingRowsCard
              title="Plates"
              description="Email the supplier when a relocation plate is reserved."
            >
              <EditableSettingRow
                label="Plate sold email"
                value={
                  supplier.email_on_line_sold
                    ? supplier.line_sold_email
                    : "Disabled"
                }
                editClassName="max-w-2xl"
                initialValues={{
                  email_on_line_sold: supplier.email_on_line_sold,
                  line_sold_email: supplier.line_sold_email,
                  line_sold_timezone: supplier.line_sold_timezone,
                  relocation_lines_valid_until:
                    supplier.relocation_lines_valid_until,
                }}
                onSave={(values) =>
                  mutateAsync({ id: supplier.id, input: values })
                }
              >
                <SupplierLineSoldForm />
              </EditableSettingRow>
            </SettingRowsCard>
          </div>
        </AdminSection>
      </div>
    </EditableRowsProvider>
  );
}

type SalesAgentRecord = NonNullable<
  NonNullable<NonNullable<SupplierRecord>["managedBy"]>["salesAgent"]
>;

function SalesAgent({ salesAgent }: { salesAgent: SalesAgentRecord }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span>{salesAgent.name}</span>
      {salesAgent.primary_email ? (
        <a
          href={`mailto:${salesAgent.primary_email}`}
          className="text-indigo-600 hover:text-indigo-500"
        >
          {salesAgent.primary_email}
        </a>
      ) : null}
      {salesAgent.primary_phone ? (
        <a href={`tel:${salesAgent.primary_phone}`} className="text-gray-500">
          {salesAgent.primary_phone}
        </a>
      ) : null}
    </div>
  );
}

function SupplierImageRow({
  supplier,
  media,
  collection,
  label,
  hint,
  frameClassName,
}: {
  supplier: NonNullable<SupplierRecord>;
  media: NonNullable<SupplierRecord>["logo"];
  collection: MediaCollection;
  label: string;
  hint: string;
  frameClassName: string;
}) {
  const dropzone = useSupplierImageDropzone({
    supplierId: supplier.id,
    collection,
  });
  const t = useTranslations("settings.general.profile");

  return (
    <DropzoneSettingRow
      label={label}
      dropzone={dropzone}
      actionLabel={t("changeButton")}
    >
      <SupplierImagePreview
        dropzone={dropzone}
        media={media}
        label={label}
        className={frameClassName}
      />
      {media ? null : <p className="text-xs text-gray-500">{hint}</p>}
    </DropzoneSettingRow>
  );
}
